<script setup>
import { ref, getCurrentInstance } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue'
import { useLoading } from '@/composables/useLoading'

defineOptions({
    layout: AdminMainLayout
})

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const passwordInput = ref(null)
const currentPasswordInput = ref(null)

const form = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
})

const updatePassword = () => {
    const routeName = proxy.route ? proxy.route('profile.password.update') : '/admin/profile/password';
    form.put(routeName, {
        errorBag: 'updatePassword',
        preserveScroll: true,
        onStart: () => showLoading('Updating password...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            form.reset()
            showSuccessMessage()
        },
        onError: () => {
            if (form.errors.password) {
                form.reset('password', 'password_confirmation')
                passwordInput.value?.$el.focus()
            }

            if (form.errors.current_password) {
                form.reset('current_password')
                currentPasswordInput.value?.$el.focus()
            }
        },
    })
}

const showSuccessMessage = () => {
    // PrimeVue toast will be handled by success message
}
</script>

<template>
    <div class="grid px-4 lg:px-0 w-full max-w-7xl mx-auto">
        <Card class="mb-6">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3 mb-2">
                        <i class="pi pi-lock text-2xl text-primary-500" />
                        <h1 class="text-2xl font-bold text-surface-900">{{ $t('Update Password') }}</h1>
                    </div>
                    <p class="text-surface-600">{{ $t('Ensure your account is using a long, random password to stay secure.') }}</p>
                </div>
            </template>

            <template #content>
                <form @submit.prevent="updatePassword" class="w-full">
                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 items-end">
                        <!-- Current Password -->
                        <div class="field">
                            <label for="current_password" class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Current Password') }}
                            </label>
                            <Password
                                id="current_password"
                                ref="currentPasswordInput"
                                v-model="form.current_password"
                                :feedback="false"
                                toggleMask
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.current_password }"
                                autocomplete="current-password"
                                :placeholder="$t('Enter your current password')"
                            />
                            <small v-if="form.errors.current_password" class="p-error">
                                {{ form.errors.current_password }}
                            </small>
                        </div>

                        <!-- New Password -->
                        <div class="field">
                            <label for="password" class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('New Password') }}
                            </label>
                            <Password
                                id="password"
                                ref="passwordInput"
                                v-model="form.password"
                                :feedback="true"
                                toggleMask
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.password }"
                                autocomplete="new-password"
                                :placeholder="$t('Enter your new password')"
                                :weakLabel="$t('Weak')"
                                :mediumLabel="$t('Medium')"
                                :strongLabel="$t('Strong')"
                            />
                            <small v-if="form.errors.password" class="p-error">
                                {{ form.errors.password }}
                            </small>
                        </div>

                        <!-- Confirm Password -->
                        <div class="field">
                            <label for="password_confirmation" class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Confirm Password') }}
                            </label>
                            <Password
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                :feedback="false"
                                toggleMask
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.password_confirmation }"
                                autocomplete="new-password"
                                :placeholder="$t('Confirm your new password')"
                            />
                            <small v-if="form.errors.password_confirmation" class="p-error">
                                {{ form.errors.password_confirmation }}
                            </small>
                        </div>

                        <!-- Actions -->
                        <div class="flex flex-row gap-2">
                            <Button
                                type="submit"
                                :label="form.processing ? $t('Updating...') : $t('Update Password')"
                                icon="pi pi-save"
                                :loading="form.processing"
                                :disabled="form.processing"
                                class="custom-button-height flex-1"
                            />
                            
                            <Button
                                type="button"
                                :label="$t('Cancel')"
                                icon="pi pi-times"
                                severity="secondary"
                                @click="() => {
                                    showLoading($t('Loading...'));
                                    router.visit(proxy.route('dashboard'), {
                                        onFinish: () => hideLoading(),
                                        onError: () => hideLoading()
                                    });
                                }"
                                class="custom-button-height flex-1"
                            />
                        </div>
                    </div>
                    <div v-if="form.recentlySuccessful" class="flex items-center gap-2 text-green-600 mt-4">
                        <i class="pi pi-check-circle" />
                        <span class="text-sm font-medium">{{ $t('Password updated successfully!') }}</span>
                    </div>
                </form>
            </template>
        </Card>
    </div>
</template>

<style scoped>
.field {
    @apply flex flex-col;
}

.p-password {
    width: 100%;
}

.custom-button-height {
    height: 42px !important;
}

.p-error {
    @apply text-red-500 text-xs mt-1;
}

.p-invalid {
    @apply border-red-500;
}
</style>